/**
 * Load SafeWalk benefit videos only when the section approaches the viewport,
 * then autoplay while the video is meaningfully visible.
 */
class ProductBenefitsVideoManager {
  constructor() {
    this.containers = Array.from(document.querySelectorAll(".custom-benefit-video-container"));
    this.loadObserver = null;
    this.playObserver = null;
  }

  init() {
    if (!this.containers.length) return;

    if (!("IntersectionObserver" in window)) {
      this.containers.forEach((container) => {
        this.load(container);
        this.play(container);
      });
      return;
    }

    this.loadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.load(entry.target);
          this.loadObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "240px 0px" },
    );

    this.playObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.35) {
            this.play(entry.target);
          } else {
            this.pause(entry.target);
          }
        });
      },
      { threshold: [0, 0.35, 0.75] },
    );

    this.containers.forEach((container) => {
      this.setupErrorHandling(container);
      this.loadObserver.observe(container);
      this.playObserver.observe(container);
    });
  }

  load(container) {
    const video = container.querySelector("video[data-lazy-autoplay]");
    if (!video || video.dataset.loaded === "true") return;

    if (video.dataset.poster) {
      video.poster = video.dataset.poster;
      video.removeAttribute("data-poster");
    }

    video.querySelectorAll("source[data-src]").forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute("data-src");
    });
    video.dataset.loaded = "true";
    video.load();
  }

  play(container) {
    const video = container.querySelector("video[data-lazy-autoplay]");
    if (!video) return;
    this.load(container);
    const playPromise = video.play();
    if (!playPromise) return;
    playPromise
      .then(() => container.removeAttribute("data-autoplay-blocked"))
      .catch(() => container.setAttribute("data-autoplay-blocked", "true"));
  }

  pause(container) {
    const video = container.querySelector("video[data-lazy-autoplay]");
    if (video && !video.paused) video.pause();
  }

  setupErrorHandling(container) {
    const video = container.querySelector("video[data-lazy-autoplay]");
    if (!video) return;
    const playButton = container.querySelector(".custom-benefit-video-play");

    video.addEventListener("loadeddata", () => {
      container.removeAttribute("data-loading");
      container.classList.add("loaded");
    });
    video.addEventListener("playing", () => {
      container.removeAttribute("data-autoplay-blocked");
    });
    video.addEventListener("error", () => {
      container.removeAttribute("data-loading");
      container.setAttribute("data-error", "true");
    });
    playButton?.addEventListener("click", () => {
      this.load(container);
      const playPromise = video.play();
      if (!playPromise) return;
      playPromise
        .then(() => container.removeAttribute("data-autoplay-blocked"))
        .catch(() => container.setAttribute("data-autoplay-blocked", "true"));
    });
  }
}

const initProductBenefitsVideos = () => {
  window.productBenefitsVideoManager = new ProductBenefitsVideoManager();
  window.productBenefitsVideoManager.init();
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initProductBenefitsVideos, { once: true });
} else {
  initProductBenefitsVideos();
}
